import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useFavoritesContext } from '@/context/FavoritesContext';
import { Heart } from 'lucide-react';

const MovieCard = ({ movie, onShowDetails, index = 0 }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavoritesContext();
  const isFav = isFavorite(movie.id);

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    toggleFavorite(movie.id);
  };

  const handleClick = useCallback(() => {
    navigate(`/movie/${movie.id}`);
  }, [movie.id, navigate]);

  return (
    <div
      className="relative flex-shrink-0 w-[160px] md:w-[200px] cursor-pointer group"
      onClick={handleClick}
    >
      {/* Poster Container */}
      <div className="relative aspect-[2/3] w-full overflow-hidden rounded-sm bg-gray-900 mb-3">
        {/* Loading Skeleton */}
        {!isLoaded && (
          <div className="absolute inset-0 bg-gray-800 animate-pulse" />
        )}

        {/* Poster Image */}
        <img
          src={movie.poster}
          alt={movie.title}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          className={cn(
            'w-full h-full object-cover transition-opacity duration-300',
            isLoaded ? 'opacity-100' : 'opacity-0'
          )}
        />

        {/* Hover Overlay with Play Button */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-10">
          <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shadow-lg transform scale-0 group-hover:scale-110 transition-transform duration-300 delay-75">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-6 h-6 ml-0.5">
              <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
            </svg>
          </div>
        </div>

        {/* BADGES OVERLAY - Mimicking Reference Image */}

        {/* BADGES OVERLAY */}

        {/* Top Left: Language & Quality (Moved here) */}
        <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
          <span className="inline-block border border-white/80 bg-black/40 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm backdrop-blur-sm uppercase shadow-sm">
            LAT
          </span>
          <span className="inline-block border border-blue-400/50 bg-blue-600/80 text-white text-[9px] font-black px-1.5 py-0.5 rounded-sm backdrop-blur-sm uppercase shadow-sm">
            HD
          </span>
        </div>

        {/* Top Right: Favorite Button */}
        <button
          onClick={handleFavoriteClick}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/20 transition-all z-20 group/fav"
        >
          <Heart
            className={cn(
              "w-4 h-4 transition-colors",
              isFav ? "fill-red-500 text-red-500" : "text-white group-hover/fav:text-red-400"
            )}
          />
        </button>

        {/* Bottom Left: TMDb Score & Rating */}
        <div className="absolute bottom-2 left-2 flex flex-col gap-1 items-start">
          <div className="flex items-center gap-1 border border-white/80 bg-black/40 px-1.5 py-0.5 rounded-sm backdrop-blur-sm">
            <span className="text-[9px] font-bold text-white tracking-tighter">TMDB</span>
            <span className="text-[10px] font-bold text-white">{movie.score || '7.5'}</span>
          </div>
          {/* Rating moved here if needed, or just remove if redundant with TMDb. Let's keep just TMDb here for cleaner look, or maybe user wants age rating 'R'? */}
          <span className="inline-block border border-white/60 bg-black/40 text-white/90 text-[9px] font-bold px-1 py-0.5 rounded-sm backdrop-blur-sm">
            {movie.rating || 'R'}
          </span>
        </div>

        {/* Bottom Right: Year */}
        <div className="absolute bottom-2 right-2">
          <span className="inline-block border border-white/80 bg-black/40 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm backdrop-blur-sm">
            {movie.year}
          </span>
        </div>
      </div>

      {/* Footer Info */}
      <div className="flex items-start justify-between gap-2 px-1">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-white truncate leading-tight mb-0.5">
            {movie.title}
          </h3>
          <p className="text-[11px] text-gray-400 truncate font-medium">
            {movie.original_title || movie.title}
          </p>
        </div>

        {/* Menu Icon */}
        <button className="text-gray-400 hover:text-white transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="1" />
            <circle cx="12" cy="5" r="1" />
            <circle cx="12" cy="19" r="1" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default MovieCard;
